import { ProviderCode } from '@interfaces/Integration';
import { plickTheme } from '@theme/index';
import { TableHeader } from '@interfaces/Table';
import { ResultFrecuentCustomers, ResultOrdersGenerated, ResultSoldProducts, StatsPath } from '@interfaces/Stats';
import type { FeedbackTemplate } from '@interfaces/Feedback';

export const weekDays = [
  { value: '1', label: 'Lunes' },
  { value: '2', label: 'Martes' },
  { value: '3', label: 'Miercoles' },
  { value: '4', label: 'Jueves' },
  { value: '5', label: 'Viernes' },
  { value: '6', label: 'Sábado' },
  { value: '0', label: 'Domingo' },
];

export const time_zones = [
  { value: 'America/Mexico_City', label: 'Ciudad de méxico' },
  { value: 'America/Cancun', label: 'Cancún' },
  { value: 'America/Chihuahua', label: 'Chihuahua' },
  { value: 'America/Ojinaga', label: 'Ciudad Juarez' },
];

export const ModuleName = {
  SCHEDULED_ORDERS: 'SCHEDULED_ORDERS',
  ESTADISTICAS: 'ESTADISTICAS', // Listar
  ESTADISTICAS_AVANZADAS: 'ESTADISTICAS_AVANZADAS', // Listar
  MENU: 'MENU', // Listar, Eliminar, Editar, Crear, Ordenar
  MENU_TEMAS: 'MENU_TEMAS',
  HISTORIAL: 'HISTORIAL', // Listar, Eliminar, Editar
  COBROS_EN_LINEA: 'COBROS_EN_LINEA', // Listar, Eliminar, Editar
  ANUNCIOS: 'ANUNCIOS', // Listar, Crear, Eliminar, Editar
  COMPARTIR: 'COMPARTIR', // Listar
  CUPONES: 'CUPONES', // Listar, Eliminar, Editar, Crear, Ordenar
  ASISTENCIA_TECNICA: 'ASISTENCIA_TECNICA', // Listar
  DATOS_COMERCIO: 'DATOS_COMERCIO', // Listar, Editar
  HORARIOS: 'HORARIOS', // Listar, Eliminar, Editar, Crear
  TARIFAS: 'TARIFAS', // Listar, Editar
  CLIENTES: 'CLIENTES',
  USUARIOS: 'USUARIOS', // Listar, Eliminar, Editar, Crear
  AJUSTES: 'AJUSTES', // Listar, Editar
  PROVEEDORES_ENTREGA: 'PROVEEDORES_ENTREGA', // Listar, Crear, Eliminar
  ENVIO_POR_KM: 'ENVIO_POR_KM', // Listar
  IMPRESION_AUTOMATICA: 'IMPRESION_AUTOMATICA', // Listar
  PROVEEDORES_TPV: 'PROVEEDORES_TPV', // Listar, Crear, Eliminar,
  LOYVERSE: 'LOYVERSE', // Integración específica Loyverse
  ORDENES_TELEFONICAS: 'ORDENES_TELEFONICAS', // Listar
  PROVEEDORES_PAGO: 'PROVEEDORES_PAGO',
  PEDIDOS_MULTISUCURSAL: 'PEDIDOS_MULTISUCURSAL', // Listar
  SEGUIMIENTO_REDES_SOCIALES: 'SEGUIMIENTO_REDES_SOCIALES', // Listar
  COVER_COMMERCE: 'COVER_COMMERCE', // Listar, Editar
  ANDROID_APP: 'ANDROID_APP', // Listar
  SELF_SERVICE: 'SELF_SERVICE', // Listar
  HANDHELD_POS: 'HANDHELD_POS', // Listar, Crear
  POR_CONSUMO: 'POR_CONSUMO', // Listar
  EDITAR_PEDIDO: 'EDITAR_PEDIDO', // Listar, Editar
  MENU_VTWO: 'MENU_VTWO', // Listar
  MASS_UPDATE_PRODUCTS: 'MASS_UPDATE_PRODUCTS', // Listar
  DELETE_BULK: 'DELETE_BULK', // Listar
  NOTIFICACIONES_CLIENTE_WHATSAPP: 'NOTIFICACIONES_CLIENTE_WHATSAPP', // Listar
  LINK_PROFILE: 'LINK_PROFILE', // Listar
  // Perfil del cliente — dos superficies con gates SEPARADOS.
  // DETALLE: badge de recurrencia dentro del detalle del pedido (sin página propia → no va en MODULES_PATH).
  PERFIL_CLIENTE_DETALLE: 'PERFIL_CLIENTE_DETALLE',
  // LISTADO: enriquece la página "Mis clientes" y habilita su entrada de menú.
  PERFIL_CLIENTE_LISTADO: 'PERFIL_CLIENTE_LISTADO',
  // Herramienta de soporte: exportar/importar secciones de extras como JSON entre
  // comercios/sucursales. Sin entrada en MODULES_PATH (no tiene página propia: son
  // acciones dentro de /menu). Se asigna por comercio desde cs.plick.app solo cuando
  // soporte lo necesita — patrón "ocultar sin upsell" (como PERFIL_CLIENTE_LISTADO).
  COPY_MENU_STRUCTURE: 'COPY_MENU_STRUCTURE',
} as const;

export const MODULES_PATH = {
  [ModuleName.HISTORIAL]: '/control-pedidos',
  [ModuleName.ESTADISTICAS]: '/estadisticas',
  [ModuleName.MENU]: '/menu',
  [ModuleName.COBROS_EN_LINEA]: '/pedidos-en-linea',
  [ModuleName.ANUNCIOS]: '/anuncios',
  [ModuleName.COMPARTIR]: '/compartir-menu',
  [ModuleName.CUPONES]: '/cupones',
  [ModuleName.ASISTENCIA_TECNICA]: '/asistencia',
  [ModuleName.DATOS_COMERCIO]: '/datos-del-comercio',
  [ModuleName.HORARIOS]: '/horarios',
  [ModuleName.TARIFAS]: '/tarifas',
  // '/clientes' se gatea por PERFIL_CLIENTE_LISTADO (el módulo nuevo de la feature), NO por el legacy
  // CLIENTES: así PageWrapper permite la ruta a quien tiene el módulo nuevo y no lo expulsa a la raíz.
  // Es la ÚNICA clave que mapea a /clientes (sin colisión).
  [ModuleName.PERFIL_CLIENTE_LISTADO]: '/clientes',
  [ModuleName.USUARIOS]: '/usuarios',
  [ModuleName.AJUSTES]: '/ajustes',
  [ModuleName.PROVEEDORES_ENTREGA]: '/tarifas/delivery-partners',
  [ModuleName.ORDENES_TELEFONICAS]: '/control-pedidos/registrar-pedido',
  [ModuleName.SELF_SERVICE]: '/mesas',
  [ModuleName.LINK_PROFILE]: '/perfil-social',
} as const;

export const defaultLatLng = {
  // Villahermosa. Mexico
  latitud: 17.983299,
  longitud: -92.948272,
};

export const defaultLatLng2 = {
  // Villahermosa. Mexico
  latitude: 17.983299,
  longitude: -92.948272,
};

export const DELIVERY_MAP = {
  delivery: {
    key: 'delivery',
    icon: 'tabler:motorbike',
    iconColor: '#00B7C2',
    color: 'color-third',
    label: 'Enviar a domicilio',
  },
  takeAway: {
    key: 'takeAway',
    icon: 'tabler:shopping-bag',
    iconColor: '#4298B5',
    color: 'color-secondary',
    label: 'Pasar a recoger',
  },
  inRestaurant: {
    key: 'inRestaurant',
    icon: 'tabler:building-store',
    iconColor: '#253746',
    color: 'color-primary',
    label: 'Comer en restaurante',
  },
} as const;

export const ORDER_COUNT = 30;
export const COUPON_COUNT = 30;

export const PAYMENT_STATUS_MAP = {
  pagado: { key: 'pagado', label: 'Pagado', color: plickTheme.colors.success },
  pendiente: { key: 'pendiente', label: 'Pendiente', color: plickTheme.colors.surface },
  cancelado: { key: 'cancelado', label: 'Cancelado', color: plickTheme.colors.dangerLight },
  fallido: { key: 'fallido', label: 'Fallido', color: plickTheme.colors.dangerLight },
} as const;

export const ORDER_STATUS_MAP = {
  scheduled: {
    key: 'scheduled',
    label: 'Programado',
    color: plickTheme.colors.secondary,
    icon: 'mdi:calendar-clock',
  },
  pre_checkout: {
    key: 'pre_checkout',
    label: 'Por confirmar',
    color: plickTheme.colors.surface,
    icon: 'mdi:clock-outline',
  },
  pending: { key: 'pending', label: 'Pendiente', color: plickTheme.colors.accent, icon: 'solar:pause-bold' },
  rejected: {
    key: 'rejected',
    label: 'Rechazado',
    color: plickTheme.colors.danger,
    icon: 'material-symbols:no-food-sharp',
  },
  accepted: { key: 'accepted', label: 'En progreso', color: plickTheme.colors.warning, icon: 'ri:fire-fill' },
  arrived_at_pickup: {
    key: 'arrived_at_pickup',
    label: 'Repartidor llegó a recoger',
    color: plickTheme.colors.help,
    icon: 'fluent:shopping-bag-add-20-filled',
  },
  on_the_way: {
    key: 'on_the_way',
    label: 'En camino',
    color: plickTheme.colors.secondary,
    icon: 'fa6-solid:motorcycle',
  },
  arrived_at_delivery: {
    key: 'arrived_at_delivery',
    label: 'Repartidor llegó',
    color: plickTheme.colors.successDark,
    icon: 'ion:home-sharp',
  },
  delivered: { key: 'delivered', label: 'Entregado', color: plickTheme.colors.primary, icon: 'fluent:food-24-filled' },
  ready_to_pick_up: {
    key: 'ready_to_pick_up',
    label: 'Listo para recoger',
    color: plickTheme.colors.primary,
    icon: 'icon-park-solid:cooking',
  },
  canceled: {
    key: 'canceled',
    label: 'Cancelado',
    color: plickTheme.colors.danger,
    icon: 'mdi:cancel-bold',
  },
} as const;

export const DELIVERY_STATUS_MAP = {
  completed: { key: 'completed', label: 'Completado' },
  pending: { key: 'pending', label: 'Pendiente' },
  canceled: {
    key: 'canceled',
    label: 'Reparto cancelado',
    color: plickTheme.colors.danger,
    icon: 'pepicons-pop:motorcycle-circle-off',
  },
} as const;

export const STATUS_ORDER_DELIVERY_PROVIDERS = [
  ORDER_STATUS_MAP.pending.key,
  ORDER_STATUS_MAP.accepted.key,
  ORDER_STATUS_MAP.arrived_at_pickup.key,
  ORDER_STATUS_MAP.on_the_way.key,
  ORDER_STATUS_MAP.arrived_at_delivery.key,
  ORDER_STATUS_MAP.delivered.key,
];

export const STATUS_ORDER_DELIVERY = [
  ORDER_STATUS_MAP.pending.key,
  ORDER_STATUS_MAP.accepted.key,
  ORDER_STATUS_MAP.on_the_way.key,
  ORDER_STATUS_MAP.delivered.key,
];

export const STATUS_ORDER_TAKE_AWAY = [
  ORDER_STATUS_MAP.pending.key,
  ORDER_STATUS_MAP.accepted.key,
  ORDER_STATUS_MAP.ready_to_pick_up.key,
  ORDER_STATUS_MAP.delivered.key,
];

export const STATUS_ORDER_IN_RESTAURANT = [
  ORDER_STATUS_MAP.pending.key,
  ORDER_STATUS_MAP.accepted.key,
  ORDER_STATUS_MAP.delivered.key,
];

export type ORDER_STATUS_ACTIONS = 'reject' | 'next' | 'prev' | 'cancel';

export const ORDER_PAYMENT_MAP = {
  efectivo: { key: 'efectivo', label: 'Efectivo', actualKey: 'Efectivo' },
  transferencia: { key: 'transferencia', label: 'Transferencia', actualKey: 'Transferencia' },
  tarjeta: { key: 'tarjeta', label: 'Tarjeta', actualKey: 'Tarjeta' },
  enlace_pago: { key: 'enlace_pago', label: 'Pago en línea Santander', actualKey: '' },
  payment_provider: { key: 'payment_provider', label: 'Pago en línea', actualKey: 'payment_provider' },
  toConfirm: { key: 'toConfirm', label: 'Por confirmar', actualKey: 'toConfirm' },
} as const;

export const PAYMENT_OPTIONS = (Object.keys(ORDER_PAYMENT_MAP) as (keyof typeof ORDER_PAYMENT_MAP)[]).map((key) => ({
  value: key,
  label: ORDER_PAYMENT_MAP[key].label,
}));

export const DELIVERY_TYPE_MAP = {
  gratis: { key: 'gratis', label: 'Gratis' },
  estandar: { key: 'estandar', label: 'Estándar' },
  por_colonia: { key: 'por_colonia', label: 'Por Colonia' },
  por_kilometro: { key: 'por_kilometro', label: 'Por Kilómetro' },
  delivery_providers: { key: 'delivery_providers', label: 'Proveedor de delivery' },
  percent: { key: 'percent', label: 'Por Porcentaje' },
} as const;

export const SOCKET_EVENTS = {
  NEW_ORDER: 'newOrder',
  // Creación de pedido programado (silencioso): va a la pestaña "Programados", NO al Kanban.
  SCHEDULED_ORDER_CREATED: 'scheduledOrderCreated',
  RIDER_ASSIGNED: 'deliveryRiderAssigned',
  CHANGE_ORDER_STATUS: 'changeOrderStatus',
  CHANGE_DELIVERY_STATUS: 'changeDeliveryStatus',
  CHANGE_PAYMENT_STATUS: 'changePaymentStatus',
  SUBSCRIPTION_CHANGED: 'subscriptionChanged',
};

export const TELEMETRY_STORAGE_KEY = 'posthogSessionId';

export const EMPTY_VALUE = '';

export const DEFAULT_COUNTRY_CODE = 52;

export const PROVIDER_TYPE_MAP = {
  delivery: { key: 'delivery', label: 'Delivery' },
  tpv: { key: 'tpv', label: 'TPV' },
  rms: { key: 'rms', label: 'Software de Gestión de Restaurantes' },
  payment: { key: 'payment', label: 'Proveedores de pago' },
  communication: { key: 'communication', label: 'Comunicación' },
};

export interface IntegrationConfiguration {
  token?: string;
  sign?: string;
  branch_code?: string;
  api_key?: string;
  secret_api_key?: string;
  secret_instance?: string;
  // Kapso fields
  kapso_api_key?: string;
  phone_number_id?: string;
  business_account_id?: string;
}

export interface Integration {
  id?: string;
  provider_code: ProviderCode;
  provider_type: keyof typeof PROVIDER_TYPE_MAP;
  configuration: IntegrationConfiguration;
}

export interface IntegrationBalance {
  name?: string;
  balance: number;
  usableBalance?: number;
  currency?: string;
}

export const NOTIFICATION_NAMES_MAP = {
  INCOMING_ORDER: {
    key: 'INCOMING_ORDER',
  },
  RIDER_ARRIVED: {
    key: 'RIDER_ARRIVED',
  },
  ORDER_DELIVERED: {
    key: 'ORDER_DELIVERED',
  },
} as const;

export const COUPON_TYPES_MAP = {
  amount: { key: 'amount', label: 'Cantidad' },
  percent: { key: 'percent', label: 'Porcentaje' },
} as const;

export const COUPON_STATUS_MAP = {
  active: { key: 'active', label: 'Activos' },
  inactive: { key: 'inactive', label: 'Inactivos' },
};

export const TypesDropdownValues = (Object.keys(COUPON_TYPES_MAP) as (keyof typeof COUPON_TYPES_MAP)[]).map((key) => ({
  value: key,
  label: COUPON_TYPES_MAP[key]?.label,
}));

export const TRIAL_DURATION_DAYS = 5;

export const SUBSCRIPTION_TYPES_MAP = {
  FREEMIUM: {
    key: 'FREEMIUM',
    label: 'freemium',
  },
  TRIAL: {
    key: 'TRIAL',
    label: 'trial',
  },
  FUNDADOR: {
    key: 'FUNDADOR',
    label: 'fundador',
  },
  BASICO: {
    key: 'BASICO',
    label: 'basico',
  },
  PROFESIONAL: {
    key: 'PROFESIONAL',
    label: 'profesional',
  },
  PLUS: {
    key: 'PLUS',
    label: 'plus',
  },
  STARTER: {
    key: 'STARTER',
    label: 'starter',
  },
};

export const PRINT_TEXT_SIZE_MAP = {
  xxs: {
    key: 'xxs',
    label: 'Muy pequeño',
  },
  small: {
    key: 'small',
    label: 'Pequeño',
  },
  medium: {
    key: 'medium',
    label: 'Mediano',
  },
  large: {
    key: 'large',
    label: 'Grande',
  },
  xxl: {
    key: 'xxl',
    label: 'Muy grande',
  },
};

export const PRINT_TEXT_SIZES_VALUES_MAP = {
  xxs: { title: '13px', subTitle: '10px', medium: '8px', content: '7px', small: '5px' },
  small: { title: '15px', subTitle: '12px', medium: '10px', content: '9px', small: '7px' },
  medium: { title: '17px', subTitle: '14px', medium: '12px', content: '11px', small: '9px' },
  large: { title: '19px', subTitle: '16px', medium: '14px', content: '13px', small: '11px' },
  xxl: { title: '21px', subTitle: '18px', medium: '16px', content: '15px', small: '13px' },
} as const;

export const PRINT_AUTO_OPTIONS_MAP = {
  onOrderReceived: {
    key: 'onOrderReceived',
    label: 'Al recibir un pedido',
  },
  onOrderAccepted: {
    key: 'onOrderAccepted',
    label: 'Al aceptar un pedido',
  },
  disabled: {
    key: 'disabled',
    label: 'Desactivado',
  },
};

export const TICKET_TYPE_MAP = {
  normal: {
    key: 'normal',
    label: 'Ticket completo',
  },
  simplified: {
    key: 'simplified',
    label: 'Ticket de cocina (simplificado)',
  },
} as const;

export const ERROR_CODE = {
  INVALID_STATUS_TRANSITION: 'INVALID_STATUS_TRANSITION',
};

export const COLORS = {
  secondary: '#4298B5',
  bgTable: '#EDF5F7',
  textBase: '#253746',
} as const;

export const EXTERNAL_URLS = {
  IDEAS: 'https://ideas.plick.com.mx/',
  FACEBOOK: 'https://www.facebook.com/plickmx',
  INSTAGRAM: 'https://www.instagram.com/plickmx/?hl=es',
  BLOG_UPDATES: 'https://blog.plick.com.mx/tag/actualizaciones-de-producto/',
  SUPPORT_WHATSAPP: 'https://wa.link/2bdzyg',
  // Número crudo al que apunta SUPPORT_WHATSAPP (resuelto del wa.link). Se usa para armar links
  // wa.me con texto PREllenado (el wa.link corto no admite `?text=`).
  SUPPORT_WHATSAPP_PHONE: '5219935318237',
  HELP_CENTER: 'https://chat.plick.com.mx/hc/centro-de-ayuda',
} as const;

export const NEWS = {
  API_URL: 'https://automate.jorgeliglabs.com/webhook/ed0d70f7-76ec-4929-815f-e2ddf800c030',
} as const;

export const STATS_FILTER_OPTIONS = {
  order_status: {
    key: 'order_status',
    label: 'Estatus del pedido',
    options: [
      { value: '', children: 'Todos' },
      { value: ORDER_STATUS_MAP.pending.key, children: ORDER_STATUS_MAP.pending.label },
      { value: ORDER_STATUS_MAP.accepted.key, children: ORDER_STATUS_MAP.accepted.label },
      { value: ORDER_STATUS_MAP.delivered.key, children: ORDER_STATUS_MAP.delivered.label },
    ],
    placeholder: 'Selecciona un estado',
  },
  delivery_type: {
    key: 'delivery_type',
    label: 'Metodo de entrega',
    options: [
      { value: '', children: 'Todos' },
      { value: DELIVERY_MAP.delivery.key, children: DELIVERY_MAP.delivery.label },
      { value: DELIVERY_MAP.takeAway.key, children: DELIVERY_MAP.takeAway.label },
      { value: DELIVERY_MAP.inRestaurant.key, children: DELIVERY_MAP.inRestaurant.label },
    ],
    placeholder: 'Selecciona un metodo de entrega',
  },
  customer_phone_number: {
    key: 'customer_phone_number',
    label: 'Teléfono del cliente',
    options: [],
    placeholder: 'Selecciona un teléfono',
  },
} as const;

// Cabeceras de la tabla de estadisticas avanzadas por tipo de tabla
export const STATS_TABLE_HEADERS: TableHeader = {
  'orders-generated': [
    'Folio',
    'Fecha',
    'Nombre de cliente',
    'Teléfono',
    'Tipo',
    'Estatus de Pedido',
    'Método de pago',
    'Estado de Pago',
    'ID de Transacción',
    'Mesa',
    'Propina',
    'Costo de envio',
    'Subtotal',
    'Total',
    'Costo de producción',
    'Ganancia',
  ],
  'cash-closing': [
    'Folio',
    'Fecha',
    'Nombre de cliente',
    'Teléfono',
    'Método de pago',
    'Propina',
    'Costo de envio',
    'Subtotal',
    'Total',
    'Costo de producción',
    'Ganancia',
  ],
  'frequent-customers': [
    'Nombre de cliente',
    'Teléfono',
    'Método de pago',
    'Metodo de Entrega',
    'Número de pedidos',
    'Total de compra acumulada',
  ],
  'sold-products': [
    'Nombre de producto',
    'Costo de producción definido',
    'Precio',
    'Cantidades de venta',
    'Total de costo de producción',
    'Total vendido',
    'Total ganancia',
    'Margen por Porcentaje',
  ],
};

// Columnas de la tabla de estadisticas avanzadas por tipo de tabla
export const STATS_TABLE_COLUMNS: Record<
  Exclude<StatsPath, 'daily-sales-statistics' | 'monthly-sales-statistics' | ''>,
  (keyof ResultOrdersGenerated | keyof ResultFrecuentCustomers | keyof ResultSoldProducts)[]
> = {
  'orders-generated': [
    'orders_count',
    'date',
    'name',
    'phone_number',
    'delivery_type',
    'order_status',
    'payment_method',
    'payment_status',
    'payment_provider_payment_id',
    'numero_mesa',
    'total_tip',
    'total_delivery',
    'subtotal',
    'ticket_total',
    'production_cost',
    'profit',
  ],
  'cash-closing': [
    'orders_count',
    'date',
    'name',
    'phone_number',
    'payment_method',
    'total_tip',
    'total_delivery',
    'subtotal',
    'ticket_total',
    'production_cost',
    'profit',
  ],
  'frequent-customers': ['name', 'phone_number', 'payment_method', 'delivery_type', 'total_orders', 'total_amount'],
  'sold-products': [
    'name',
    'production_cost',
    'price',
    'total_quantity',
    'total_production_cost',
    'total_sold',
    'total_revenue',
    'total_margin',
  ],
};

export const PAYMENT_PROVIDER_REASON_MAP: Record<string, string> = {
  INSUFFICIENT_FUNDS: 'Fondos insuficientes',
  LIMIT_EXCEEDED: 'Límite excedido',
  CARD_EXPIRED: 'Tarjeta expirada',
  CARD_DISABLED: 'Tarjeta deshabilitada',
  CARD_INVALID: 'Tarjeta inválida',
  CARD_LOST: 'Tarjeta perdida',
  CARD_STOLEN: 'Tarjeta robada',
  INVALID_CVV: 'CVV incorrecto',
  INVALID_CARD_NUMBER: 'Número inválido',
  INVALID_EXPIRY_DATE: 'Fecha inválida',
  INVALID_DATA: 'Datos incorrectos',
  BANK_DECLINED: 'Rechazado por banco',
  BANK_AUTHORIZATION_REQUIRED: 'Requiere autorización',
  FRAUD_SUSPECTED: 'Sospecha de fraude',
  HIGH_RISK: 'Alto riesgo',
  BLACKLISTED: 'Tarjeta no permitida',
  PAYMENT_INTENT_MISMATCH: 'Intent no coincide',
  MAX_ATTEMPTS_EXCEEDED: 'Intentos excedidos',
  DUPLICATE_PAYMENT: 'Pago duplicado',
  PROCESSING_ERROR: 'Error de procesamiento',
  NETWORK_ERROR: 'Error de conexión',
  TIMEOUT: 'Tiempo agotado',
  PROVIDER_ERROR: 'Error del proveedor',
  UNKNOWN: 'Error desconocido',
  CANCELLED_BY_USER: 'Cancelado por usuario',
};

export const getPaymentReasonLabel = (reason: string): string => {
  return PAYMENT_PROVIDER_REASON_MAP[reason] || reason;
};

// ===================================
// FEEDBACK SYSTEM CONSTANTS
// ===================================

export const FEEDBACK_MIN_CHARACTERS = 100;

export const FEEDBACK_TEMPLATES: FeedbackTemplate[] = [
  {
    id: 1,
    label: 'Necesito hacer algo que Plick no me deja',
    value: 'Necesito poder ',
    placeholder: 'Ej: programar descuentos por día de la semana',
  },
  {
    id: 2,
    label: 'Algo me frustra o me toma mucho tiempo',
    value: 'Me frustra que ',
    placeholder: 'Ej: configurar horarios de envío es complicado',
  },
  {
    id: 3,
    label: 'Plick me ha ayudado con algo específico',
    value: 'Gracias a Plick ahora ',
    placeholder: 'Ej: ahorro 2 horas diarias organizando pedidos',
  },
  {
    id: 4,
    label: 'Tengo una idea para mejorar mi operación',
    value: 'Sería más fácil si ',
    placeholder: 'Ej: pudiera duplicar productos en el menú',
  },
];
